













import axios from "@/libs/axios";
import useEmbeddedSignup from "@/composables/v3/useEmbeddedSignup";
import { BButton, BSpinner } from "bootstrap-vue";
import { computed, defineComponent, onMounted, ref, watch } from "@vue/composition-api";
import { storeToRefs } from "pinia";
import { useCustomersStore } from "@/store/useCustomersStore";
import { useToast } from "vue-toastification/composition";
import i18n from "@/libs/i18n";

type EventResponseData = {
    type: string; // "WA_EMBEDDED_SIGNUP"
    data:
        | { phone_number_id: string; waba_id: string }
        | { error_message: string; error_id: string; session_id: string; timestamp: string }
        | { current_step: string };
    event: string; // "FINISHED", "CANCEL", ...
    version: number; // 3
};

export default defineComponent({
    components: { BButton, BSpinner },
    setup() {
        const initialDisabled = ref(true);
        const whatsappChannels = ref(null);

        const { launchWhatsAppSignup, isSignupInProgress, isSetupInProgress, initMetaSdk, wabaId, phoneNumberId } =
            useEmbeddedSignup();

        onMounted(() => {
            initMetaSdk();

            window.addEventListener("message", (event: Event & { origin: string; data: string }) => {
                if (event.origin !== "https://www.facebook.com" && event.origin !== "https://web.facebook.com") {
                    return;
                }

                try {
                    const data: EventResponseData = JSON.parse(event.data);
                    if (data.type === "WA_EMBEDDED_SIGNUP") {
                        wabaId.value = "waba_id" in data.data ? data.data.waba_id : undefined;
                        phoneNumberId.value = "phone_number_id" in data.data ? data.data.phone_number_id : undefined;
                    }
                } catch {
                    //
                }
            });
        });

        const fetchWhatsappChannels = () => {
            return axios
                .get("channels", {
                    params: {
                        fields: "name,provider,platform,identity,active",
                        s: {
                            $or: [{ platform: { $cont: "whatsapp" } }, { provider: { $cont: "whatsapp" } }],
                        },
                    },
                })
                .then((res) => {
                    whatsappChannels.value = res.data;

                    initialDisabled.value = false;
                });
        };
        fetchWhatsappChannels();

        const hasReachedLimit = computed(() => {
            const { customerSettings } = storeToRefs(useCustomersStore());

            const channels = whatsappChannels.value?.filter(
                (channel) => channel.provider === "whatsapp" && channel.platform === "whatsapp"
            );

            return channels?.length >= customerSettings.value.embeddedSignupLimit;
        });

        const hasEnvConfiguration = () => {
            return !!(
                process.env.VUE_APP_META_APP_ID &&
                process.env.VUE_APP_META_APP_SECRET &&
                process.env.VUE_APP_META_CONFIG_ID
            );
        };

        const isButtonDisabled = computed(() => {
            return (
                isSignupInProgress.value || isSetupInProgress.value || hasReachedLimit.value || !hasEnvConfiguration()
            );
        });

        const toast = useToast();
        const openPopup = async () => {
            launchWhatsAppSignup((err) => {
                if (err) {
                    toast.error(typeof err === "string" ? err : i18n.t("embedded-signup.failed"));
                    return;
                }

                toast.success(i18n.t("embedded-signup.success"));
            });
        };

        const buttonTitle = computed(() => {
            if (!hasEnvConfiguration()) {
                return "No valid configuration found for embedded signup. Please contact Saysimple.";
            }

            if (hasReachedLimit.value) {
                return i18n.t("embedded-signup.limit-reached");
            }
        });

        return {
            buttonTitle,
            hasReachedLimit,
            initialDisabled,
            isButtonDisabled,
            isSetupInProgress,
            isSignupInProgress,
            openPopup,
        };
    },
});
